import { useMemo } from 'react';
import { components } from '@lifeserver/models/portal';
import {
    skipToken,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';
import _ from 'lodash';

import { isPostData, PostData } from '../portal/model/PostTypes';
import { isTemplateId, PortalPostState, TemplateId } from '@lifeserver/shared';

import { assert } from '../utils/assert';

/**
 * Help! What is this scary type?! Well, payload in the post type is supposed to
 * allow any possible data. However, due to a combination of nestjs openapi
 * generation <-> openapi-ts generator, we can't consistently get the payload to
 * generate a 'any' or 'unknown' type to use. Instead, we transform the
 * generated types using the typescript type system.
 *
 * Explanation
 *  - Is the type a post? If yes, transform payload and templateId to our internal types
 *  - Is the type an object? Do a recursive check on all child items
 *  - Is the type an array? Do a recursive check on the array elements
 *  - Otherwise, just return the type
 */
type NormalizePostType<Type> =
    Type extends Pick<
        components['schemas']['PortalPostSlimDTO'],
        'payload' | 'templateId' | 'state' | 'notes'
    >
        ? Omit<Type, 'payload' | 'templateId' | 'state'> & {
              payload: PostData;
              templateId: TemplateId;
              state: PortalPostState;
          }
        : Type extends { [key: string]: unknown }
          ? {
                [Key in keyof Type]: NormalizePostType<Type[Key]>;
            }
          : Type extends Array<infer ArrayItem>
            ? Array<NormalizePostType<ArrayItem>>
            : Type;

export type Post = NormalizePostType<components['schemas']['PortalPostDTO']>;
export type CreatePostDTO = NormalizePostType<
    components['schemas']['CreatePortalPostDTO']
>;
export type UpdatePostDTO = NormalizePostType<
    components['schemas']['UpdatePortalPostDTO']
>;

export type ScheduleDTO = NormalizePostType<
    components['schemas']['CategoryScheduleDTO']
>;
export type PutScheduleDto = components['schemas']['PutPortalScheduleDTO'];
export type PublicationDTO = NormalizePostType<
    ScheduleDTO['publications'][number]
>;

export type PortalFeaturedPostsDTO = NormalizePostType<
    components['schemas']['PortalFeaturedPostsDTO']
>;
export type PutFeaturedPostsDTO = NormalizePostType<
    components['schemas']['PutPortalFeaturedPostsDTO']
>;
export type FeaturedPublicationDTO = NormalizePostType<
    PortalFeaturedPostsDTO['featuredPosts'][number]
>;
export type PortalPostSlimDTO = NormalizePostType<
    components['schemas']['PortalPostSlimDTO']
>;
export type PollResultDto = NormalizePostType<
    components['schemas']['PollResultDto']
>;
export type GetPollOverviewDto = NormalizePostType<
    components['schemas']['GetPollOverviewDto']
>;
export type PortalCategoryDTO = NormalizePostType<
    components['schemas']['CategoryDTO']
>;
export type UpsertPortalCategoryDTO = NormalizePostType<
    components['schemas']['UpsertPortalCategoryDTO']
>;
export type CategoryScheduleDTO = NormalizePostType<
    components['schemas']['CategoryScheduleDTO']
>;
export type PutCategoryScheduleDTO = NormalizePostType<
    components['schemas']['PutCategoryScheduleDTO']
>;
export type GetPortalTagDTO = NormalizePostType<
    components['schemas']['PortalTagDTO']
>;
export type PortalLocalizationDTO =
    components['schemas']['PortalLocalizationDTO'];

export type CreatePortalLocalizationDTO =
    components['schemas']['CreatePortalLocalizationDTO'];
export type PortalPostLocalizationDTO =
    components['schemas']['UpsertPortalPostLocalizationDTO'];

export type PortalSettingsDTO = NormalizePostType<
    components['schemas']['PortalSettingsDTO']
>;
export type UpsertSettingsBodyDTO = NormalizePostType<
    components['schemas']['UpsertSettingsBodyDTO']
>;
type GetManySettingsDTO = NormalizePostType<
    components['schemas']['GetManySettingsDTO']
>;
export type PortalLocalizationMetaDataDTO =
    components['schemas']['PortalLocalizationMetaDataDTO'];

export type PortalFeedDTO = components['schemas']['PortalFeedDTO'];

const isPost = (value: unknown): value is Post => {
    return (
        value !== null &&
        typeof value === 'object' &&
        'payload' in value &&
        'templateId' in value &&
        typeof value.templateId === 'string' &&
        isPostData(value.payload) &&
        isTemplateId(value.templateId)
    );
};
const isPortalCategory = (value: unknown): value is PortalCategoryDTO => {
    return (
        value !== null &&
        typeof value === 'object' &&
        'id' in value &&
        'name' in value &&
        typeof value.id === 'string' &&
        typeof value.name === 'string'
    );
};

const DEFAULT_BASE_PATH = '/api';
const PORTAL_URL = '/portal/v2';
export const PORTAL_POST_URL = `${PORTAL_URL}/post`;
export const PORTAL_SCHEDULE_URL = `${PORTAL_URL}/schedule`;
export const PORTAL_FEATURED_POSTS_URL = `${PORTAL_URL}/featured`;
const PORTAL_POLL_URL = `${PORTAL_POST_URL}/poll`;
export const PORTAL_CATEGORY_URL = `${PORTAL_URL}/category`;
const PORTAL_TAGS_URL = `${PORTAL_URL}/post/tags`;
const PORTAL_LOCALIZATIONS_URL = `${PORTAL_URL}/localizations`;
const PORTAL_SETTINGS_URL = `${PORTAL_URL}/settings`;
const PORTAL_FEED_URL = `${PORTAL_URL}/feed`;

export class PortalApi {
    fetchPostById = async ({
        id,
        basePath = DEFAULT_BASE_PATH,
    }: {
        id: string;
        basePath?: string;
    }): Promise<Post> => {
        const { data } = await axios.get<
            components['schemas']['PortalPostDTO']
        >(`${basePath}${PORTAL_POST_URL}/${id}`);
        assert(isPost(data));
        return data;
    };

    fetchPosts = async ({
        basePath = DEFAULT_BASE_PATH,
    }: {
        basePath?: string;
    } = {}): Promise<Array<Post>> => {
        const { data } = await axios.get<Array<Post>>(
            `${basePath}${PORTAL_POST_URL}`,
        );

        return data;
    };

    createPost = async ({
        createPostDto,
        basePath = DEFAULT_BASE_PATH,
    }: {
        createPostDto: CreatePostDTO;
        basePath?: string;
    }): Promise<Post> => {
        const response = await axios.post<Post>(
            `${basePath}${PORTAL_POST_URL}`,
            createPostDto,
        );

        assert(isPost(response.data));

        return response.data;
    };

    deletePost = async ({
        postId,
        basePath = DEFAULT_BASE_PATH,
    }: {
        postId: string;
        basePath?: string;
    }): Promise<void> => {
        await axios.delete(`${basePath}${PORTAL_POST_URL}/${postId}`);
    };

    updatePost = async ({
        postId,
        updatePostDto,
        basePath = DEFAULT_BASE_PATH,
    }: {
        postId: string;
        updatePostDto: UpdatePostDTO;
        basePath?: string;
    }): Promise<Post> => {
        const response = await axios.put<Post>(
            `${basePath}${PORTAL_POST_URL}/${postId}`,
            updatePostDto,
        );

        assert(isPost(response.data));

        return response.data;
    };
    getScheduleByCategoryId = async ({
        categoryId,
        basePath = DEFAULT_BASE_PATH,
    }: {
        categoryId: string;
        basePath?: string;
    }): Promise<ScheduleDTO> => {
        const { data } = await axios.get<ScheduleDTO>(
            `${basePath}${PORTAL_SCHEDULE_URL}/${categoryId}`,
        );
        return data;
    };
    saveScheduleByCategoryId = async ({
        categoryId,
        updateScheduleByCategoryDto,
        basePath = DEFAULT_BASE_PATH,
    }: {
        categoryId: string;
        updateScheduleByCategoryDto: PutCategoryScheduleDTO;
        basePath?: string;
    }): Promise<PutCategoryScheduleDTO> => {
        const { data } = await axios.put<PutCategoryScheduleDTO>(
            `${basePath}${PORTAL_SCHEDULE_URL}/${categoryId}`,
            updateScheduleByCategoryDto,
        );

        return data;
    };

    getFeaturedPosts = async ({
        basePath = DEFAULT_BASE_PATH,
    }: {
        basePath?: string;
    } = {}): Promise<PortalFeaturedPostsDTO> => {
        const response = await axios.get<PortalFeaturedPostsDTO>(
            `${basePath}${PORTAL_FEATURED_POSTS_URL}`,
        );

        return response.data;
    };

    saveFeaturedPosts = async ({
        putFeaturedPostsDto,
        basePath = DEFAULT_BASE_PATH,
    }: {
        putFeaturedPostsDto: PutFeaturedPostsDTO;
        basePath?: string;
    }): Promise<PortalFeaturedPostsDTO> => {
        const response = await axios.put<PortalFeaturedPostsDTO>(
            `${basePath}${PORTAL_FEATURED_POSTS_URL}`,
            putFeaturedPostsDto,
        );

        return response.data;
    };

    getPollResult = async ({
        pollId,
        basePath = DEFAULT_BASE_PATH,
    }: {
        pollId: string;
        basePath?: string;
    }): Promise<PollResultDto> => {
        const response = await axios.get<PollResultDto>(
            `${basePath}${PORTAL_POLL_URL}/${pollId}`,
        );
        return response.data;
    };

    getPollOverview = async ({
        basePath = DEFAULT_BASE_PATH,
    }: {
        basePath?: string;
    } = {}): Promise<GetPollOverviewDto> => {
        const response = await axios.get<GetPollOverviewDto>(
            `${basePath}${PORTAL_POLL_URL}/all/overview`,
        );
        return response.data;
    };

    fetchCategoryById = async ({
        id,
        basePath = DEFAULT_BASE_PATH,
    }: {
        id: string;
        basePath?: string;
    }): Promise<PortalCategoryDTO> => {
        const { data } = await axios.get<components['schemas']['CategoryDTO']>(
            `${basePath}${PORTAL_CATEGORY_URL}/${id}`,
        );

        assert(isPortalCategory(data));

        return data;
    };

    fetchCategories = async ({
        basePath = DEFAULT_BASE_PATH,
    }: {
        basePath?: string;
    } = {}): Promise<Array<PortalCategoryDTO>> => {
        const { data } = await axios.get<Array<PortalCategoryDTO>>(
            `${basePath}${PORTAL_CATEGORY_URL}`,
        );

        return data;
    };

    updateCategory = async ({
        categoryId,
        upsertPortalCategoryDTO,
        basePath = DEFAULT_BASE_PATH,
    }: {
        categoryId: string;
        upsertPortalCategoryDTO: UpsertPortalCategoryDTO;
        basePath?: string;
    }): Promise<PortalCategoryDTO> => {
        try {
            const response = await axios.put<PortalCategoryDTO>(
                `${basePath}${PORTAL_CATEGORY_URL}/${categoryId}`,
                upsertPortalCategoryDTO,
            );
            assert(isPortalCategory(response.data));

            return response.data;
        } catch (error) {
            console.error('Error updating category:', error);
            throw error;
        }
    };
    deleteCategory = async ({
        categoryId,
        basePath = DEFAULT_BASE_PATH,
    }: {
        categoryId: string;
        basePath?: string;
    }): Promise<void> => {
        await axios.delete(`${basePath}${PORTAL_CATEGORY_URL}/${categoryId}`);
    };
    fetchTags = async ({
        basePath = DEFAULT_BASE_PATH,
    }: {
        basePath?: string;
    } = {}): Promise<Array<GetPortalTagDTO>> => {
        const { data } = await axios.get<Array<GetPortalTagDTO>>(
            `${basePath}${PORTAL_TAGS_URL}`,
        );

        return data;
    };
    getTranslations = async ({
        basePath = DEFAULT_BASE_PATH,
    }: {
        basePath?: string;
    } = {}): Promise<PortalLocalizationDTO[]> => {
        const { data } = await axios.get<PortalLocalizationDTO[]>(
            `${basePath}${PORTAL_LOCALIZATIONS_URL}`,
        );

        return data;
    };
    getTranslationsWithMetaData = async ({
        basePath = DEFAULT_BASE_PATH,
    }: {
        basePath?: string;
    } = {}): Promise<PortalLocalizationMetaDataDTO[]> => {
        const { data } = await axios.get<PortalLocalizationMetaDataDTO[]>(
            `${basePath}${PORTAL_LOCALIZATIONS_URL}/metadata`,
        );

        return data;
    };

    saveTranslation = async ({
        localizations,
        basePath = DEFAULT_BASE_PATH,
    }: {
        localizations: Array<CreatePortalLocalizationDTO>;
        basePath?: string;
    }): Promise<Array<PortalLocalizationDTO>> => {
        const response = await axios.put<Array<PortalLocalizationDTO>>(
            `${basePath}${PORTAL_LOCALIZATIONS_URL}/batch`,
            { localizations },
        );
        return response.data;
    };

    fetchSettings = async ({
        basePath = DEFAULT_BASE_PATH,
    }: {
        basePath?: string;
    } = {}): Promise<Array<PortalSettingsDTO>> => {
        const { data } = await axios.get<GetManySettingsDTO>(
            `${basePath}${PORTAL_SETTINGS_URL}`,
        );

        return data.settings;
    };

    fetchSettingById = async ({
        id,
        basePath = DEFAULT_BASE_PATH,
    }: {
        id: string;
        basePath?: string;
    }): Promise<PortalSettingsDTO> => {
        const { data } = await axios.get<PortalSettingsDTO>(
            `${basePath}${PORTAL_SETTINGS_URL}/${id}`,
        );

        return data;
    };

    upsertSetting = async ({
        id,
        upsertSettingsBodyDTO,
        basePath = DEFAULT_BASE_PATH,
    }: {
        id: string;
        upsertSettingsBodyDTO: UpsertSettingsBodyDTO;
        basePath?: string;
    }): Promise<PortalSettingsDTO> => {
        const { data } = await axios.put<PortalSettingsDTO>(
            `${basePath}${PORTAL_SETTINGS_URL}/${id}`,
            upsertSettingsBodyDTO,
        );

        return data;
    };

    deleteSetting = async ({
        id,
        basePath = DEFAULT_BASE_PATH,
    }: {
        id: string;
        basePath?: string;
    }): Promise<void> => {
        await axios.delete(`${basePath}${PORTAL_SETTINGS_URL}/${id}`);
    };

    setActiveSetting = async ({
        id,
        basePath = DEFAULT_BASE_PATH,
    }: {
        id: string;
        basePath?: string;
    }): Promise<PortalSettingsDTO> => {
        const { data } = await axios.put<PortalSettingsDTO>(
            `${basePath}${PORTAL_SETTINGS_URL}/${id}/active`,
        );

        return data;
    };

    getPortalFeed = async ({
        basePath = DEFAULT_BASE_PATH,
    }: {
        basePath?: string;
    } = {}): Promise<PortalFeedDTO> => {
        const { data } = await axios.get<PortalFeedDTO>(
            `${basePath}${PORTAL_FEED_URL}`,
        );

        return data;
    };
}

export const usePortalApi = () => {
    return useMemo(() => new PortalApi(), []);
};

export const usePortalPost = (id?: string) => {
    const portalApi = usePortalApi();
    return useQuery({
        queryKey: [PORTAL_POST_URL, id],
        queryFn: id ? () => portalApi.fetchPostById({ id }) : skipToken,
    });
};

export const useCreatePortalPost = () => {
    const portalApi = usePortalApi();
    const client = useQueryClient();

    return useMutation({
        mutationFn: portalApi.createPost,
        onMutate: async ({ createPostDto }) => {
            await client.cancelQueries({ queryKey: [PORTAL_POST_URL] });
            const previousPosts =
                client.getQueryData<Array<Post>>([PORTAL_POST_URL]) ?? [];
            const newPosts: Array<Post> = [
                ...previousPosts,
                {
                    ...createPostDto,
                    publications: [],
                    createdAt: new Date().toISOString(),
                    modifiedAt: new Date().toISOString(),
                    modifiedBy: null,
                    createdBy: null,
                    minimumVersion: null,
                },
            ];
            client.setQueryData([PORTAL_POST_URL], newPosts);

            return { previousPosts, newPosts };
        },
        onError: (_, __, context) => {
            client.setQueryData([PORTAL_POST_URL], context?.previousPosts);
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: [PORTAL_POST_URL] });
        },
    });
};

export const useDuplicatePortalPost = () => {
    const portalApi = usePortalApi();
    const createPost = useCreatePortalPost();

    const mutation = useMutation({
        mutationFn: async ({
            postId,
            newName,
        }: {
            postId: Post['id'];
            newName: string;
        }) => {
            const sourcePost = await portalApi.fetchPostById({ id: postId });

            return createPost.mutateAsync({
                createPostDto: {
                    id: newName,
                    templateId: sourcePost.templateId,
                    payload: _.cloneDeep(sourcePost.payload),
                    state: 'todo',
                    notes: sourcePost.notes ?? '',
                    tags: sourcePost.tags ?? [],
                    minimumVersion: sourcePost.minimumVersion,
                    postLocalizations:
                        sourcePost.postLocalizations?.map(
                            ({ portalLocalizationId, path }) => ({
                                portalLocalizationId,
                                portalPostId: newName,
                                path,
                            }),
                        ) ?? [],
                },
            });
        },
    });

    return mutation;
};

export const useUpdatePortalPost = () => {
    const portalApi = usePortalApi();
    const client = useQueryClient();

    return useMutation({
        mutationFn: portalApi.updatePost,
        onMutate: async ({ postId, updatePostDto }) => {
            await client.cancelQueries({ queryKey: [PORTAL_POST_URL] });
            const previousPosts =
                client.getQueryData<Array<Post>>([PORTAL_POST_URL]) ?? [];
            const newPosts: Array<Post> = previousPosts.map((post) =>
                post.id === postId ? { ...post, ...updatePostDto } : post,
            );
            client.setQueryData([PORTAL_POST_URL], newPosts);

            return { previousPosts, newPosts };
        },
        onError: (_, __, context) => {
            client.setQueryData([PORTAL_POST_URL], context?.previousPosts);
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: [PORTAL_POST_URL] });
        },
    });
};

export const usePortalPosts = () => {
    const portalApi = usePortalApi();
    const client = useQueryClient();

    const query = useQuery({
        queryKey: [PORTAL_POST_URL],
        queryFn: () => portalApi.fetchPosts(),
    });

    const createPost = useCreatePortalPost();

    const deletePost = useMutation({
        mutationFn: portalApi.deletePost,
        onMutate: async ({ postId }) => {
            await client.cancelQueries({ queryKey: [PORTAL_POST_URL] });
            const previousPosts =
                client.getQueryData<Array<Post>>([PORTAL_POST_URL]) ?? [];
            const newPosts = previousPosts.filter((post) => post.id !== postId);
            client.setQueryData([PORTAL_POST_URL], newPosts);

            return { previousPosts, newPosts };
        },
        onError: (_, __, context) => {
            client.setQueryData([PORTAL_POST_URL], context?.previousPosts);
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: [PORTAL_POST_URL] });
        },
    });

    const updatePost = useUpdatePortalPost();

    return { ...query, createPost, deletePost, updatePost };
};
export const useSaveScheduleByCategory = () => {
    const portalApi = usePortalApi();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({
            categoryId,
            updateScheduleByCategoryDto,
            basePath = DEFAULT_BASE_PATH,
        }: {
            categoryId: string;
            updateScheduleByCategoryDto: PutCategoryScheduleDTO;
            basePath?: string;
        }) => {
            const { publications } = await portalApi.saveScheduleByCategoryId({
                categoryId,
                updateScheduleByCategoryDto,
                basePath,
            });
            return publications;
        },
        onMutate: async ({ categoryId, updateScheduleByCategoryDto }) => {
            await client.cancelQueries({ queryKey: [PORTAL_SCHEDULE_URL] });

            const previousSchedules =
                client.getQueryData<CategoryScheduleDTO[]>([
                    PORTAL_SCHEDULE_URL,
                ]) ?? [];

            const newSchedules = previousSchedules.map((schedule) =>
                schedule.categoryId === categoryId
                    ? {
                          ...schedule,
                          publications:
                              updateScheduleByCategoryDto.publications.map(
                                  (pub) => ({
                                      ...pub,
                                      startTime: new Date(pub.startTime),
                                      endTime: pub.endTime
                                          ? new Date(pub.endTime)
                                          : null,
                                  }),
                              ),
                      }
                    : schedule,
            );

            client.setQueryData([PORTAL_SCHEDULE_URL], newSchedules);

            return { previousSchedules };
        },
        onError: (_, __, context) => {
            if (context?.previousSchedules) {
                client.setQueryData(
                    [PORTAL_SCHEDULE_URL],
                    context.previousSchedules,
                );
            }
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: [PORTAL_SCHEDULE_URL] });
        },
    });
};
export const usePortalSchedule = (categoryId?: string) => {
    const portalApi = usePortalApi();
    return useQuery({
        queryKey: [PORTAL_SCHEDULE_URL, categoryId],
        queryFn: categoryId
            ? () => portalApi.getScheduleByCategoryId({ categoryId })
            : skipToken,
    });
};

const useSavePortalFeaturedPosts = () => {
    const portalApi = usePortalApi();
    const client = useQueryClient();

    return useMutation({
        mutationFn: portalApi.saveFeaturedPosts,
        onSettled: (_, __, data) => {
            client.invalidateQueries({
                queryKey: [PORTAL_FEATURED_POSTS_URL, data.basePath],
            });
        },
    });
};

export const usePortalFeaturedPosts = () => {
    const portalApi = usePortalApi();
    const saveFeaturedPosts = useSavePortalFeaturedPosts();

    const query = useQuery({
        queryKey: [PORTAL_FEATURED_POSTS_URL],
        queryFn: () => portalApi.getFeaturedPosts(),
    });

    return { ...query, saveFeaturedPosts };
};

export const usePollResult = (pollId?: string) => {
    const portalApi = usePortalApi();
    return useQuery({
        queryKey: [PORTAL_POLL_URL, pollId],
        queryFn: pollId ? () => portalApi.getPollResult({ pollId }) : skipToken,
    });
};

export const usePortalCategories = () => {
    const portalApi = usePortalApi();
    const client = useQueryClient();

    const query = useQuery({
        queryKey: [PORTAL_CATEGORY_URL],
        queryFn: () => portalApi.fetchCategories(),
    });
    const upsertCategory = useUpsertPortalCategory();
    const deleteCategory = useMutation({
        mutationFn: portalApi.deleteCategory,
        onMutate: async ({ categoryId }) => {
            await client.cancelQueries({ queryKey: [PORTAL_CATEGORY_URL] });
            const previousCategories =
                client.getQueryData<Array<PortalCategoryDTO>>([
                    PORTAL_CATEGORY_URL,
                ]) ?? [];
            const newCategories = previousCategories.filter(
                (category) => category.id !== categoryId,
            );
            client.setQueryData([PORTAL_CATEGORY_URL], newCategories);

            return { previousCategories, newCategories };
        },
        onError: (_, __, context) => {
            client.setQueryData(
                [PORTAL_CATEGORY_URL],
                context?.previousCategories,
            );
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: [PORTAL_CATEGORY_URL] });
        },
    });

    return { ...query, upsertCategory, deleteCategory };
};

export const useUpsertPortalCategory = () => {
    const portalApi = usePortalApi();
    const client = useQueryClient();
    return useMutation({
        mutationFn: portalApi.updateCategory,
        onMutate: async ({
            categoryId,
            upsertPortalCategoryDTO: updateCategoryDto,
        }) => {
            await client.cancelQueries({ queryKey: [PORTAL_CATEGORY_URL] });
            const previousCategories =
                client.getQueryData<Array<PortalCategoryDTO>>([
                    PORTAL_CATEGORY_URL,
                ]) ?? [];
            const newCategories: Array<PortalCategoryDTO> =
                previousCategories.map((category) =>
                    category.id === categoryId
                        ? { ...category, ...updateCategoryDto }
                        : category,
                );

            newCategories.sort(
                (catA, catB) =>
                    (catA.position ?? Number.MAX_SAFE_INTEGER) -
                    (catB.position ?? Number.MAX_SAFE_INTEGER),
            );
            client.setQueryData([PORTAL_CATEGORY_URL], newCategories);

            return { previousCategories, newCategories };
        },
        onError: (_, __, context) => {
            client.setQueryData(
                [PORTAL_CATEGORY_URL],
                context?.previousCategories,
            );
        },
        onSettled: (_, __, variables) => {
            client.invalidateQueries({ queryKey: [PORTAL_CATEGORY_URL] });
            client.invalidateQueries({
                queryKey: [PORTAL_SCHEDULE_URL, variables.categoryId],
            });
            client.invalidateQueries({ queryKey: [PORTAL_SETTINGS_URL] });
        },
    });
};
export const usePortalTags = () => {
    const portalApi = usePortalApi();
    return useQuery({
        queryKey: [PORTAL_TAGS_URL],
        queryFn: () => portalApi.fetchTags(),
    });
};

export const usePortalLocalization = () => {
    const portalApi = usePortalApi();
    return useQuery({
        queryKey: [PORTAL_LOCALIZATIONS_URL],
        queryFn: () => portalApi.getTranslations(),
    });
};
export const usePortalLocalizationWithMetaData = () => {
    const portalApi = usePortalApi();
    return useQuery({
        queryKey: [PORTAL_LOCALIZATIONS_URL, 'metadata'],
        queryFn: () => portalApi.getTranslationsWithMetaData(),
    });
};
function mergeLocalizations(
    previous: PortalLocalizationDTO[],
    patch: CreatePortalLocalizationDTO[],
): PortalLocalizationDTO[] {
    const map = new Map<string, PortalLocalizationDTO>();
    for (const loc of previous) {
        map.set(`${loc.id}:${loc.language}`, loc);
    }
    for (const loc of patch) {
        map.set(`${loc.id}:${loc.language}`, loc as PortalLocalizationDTO);
    }
    return Array.from(map.values());
}
export const useSavePortalLocalization = () => {
    const portalApi = usePortalApi();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({
            localizations,
            basePath,
        }: {
            localizations: Array<CreatePortalLocalizationDTO>;
            basePath?: string;
        }) => {
            return portalApi.saveTranslation({ localizations, basePath });
        },
        onMutate: async ({ localizations }) => {
            await client.cancelQueries({
                queryKey: [PORTAL_LOCALIZATIONS_URL],
            });
            await client.cancelQueries({
                queryKey: [PORTAL_LOCALIZATIONS_URL, 'metadata'],
            });
            const previousLocalizations =
                client.getQueryData<Array<PortalLocalizationDTO>>([
                    PORTAL_LOCALIZATIONS_URL,
                ]) ?? [];
            const safePreviousLocalizations = Array.isArray(
                previousLocalizations,
            )
                ? previousLocalizations
                : [];

            const newLocalizations = mergeLocalizations(
                safePreviousLocalizations,
                localizations,
            );

            client.setQueryData([PORTAL_LOCALIZATIONS_URL], newLocalizations);
            client.setQueryData(
                [PORTAL_LOCALIZATIONS_URL, 'metadata'],
                newLocalizations,
            );
            return { previousLocalizations, newLocalizations };
        },
        onError: (_, __, context) => {
            client.setQueryData(
                [PORTAL_LOCALIZATIONS_URL],
                context?.previousLocalizations,
            );
        },
        onSettled: () => {
            client.invalidateQueries({
                queryKey: [PORTAL_LOCALIZATIONS_URL, 'metadata'],
            });
            client.invalidateQueries({ queryKey: [PORTAL_LOCALIZATIONS_URL] });
        },
    });
};

export const usePortalPollOverview = () => {
    const portalApi = usePortalApi();
    return useQuery({
        queryKey: [PORTAL_POLL_URL, 'overview'],
        queryFn: () => portalApi.getPollOverview(),
    });
};

export const usePortalSettings = () => {
    const portalApi = usePortalApi();
    const client = useQueryClient();

    const query = useQuery({
        queryKey: [PORTAL_SETTINGS_URL],
        queryFn: () => portalApi.fetchSettings(),
    });

    const upsertSetting = useMutation({
        mutationFn: portalApi.upsertSetting,
        onSettled: () => {
            client.invalidateQueries({ queryKey: [PORTAL_SETTINGS_URL] });
        },
    });

    const deleteSetting = useMutation({
        mutationFn: portalApi.deleteSetting,
        onMutate: async ({ id }) => {
            await client.cancelQueries({ queryKey: [PORTAL_SETTINGS_URL] });
            const previousSettings =
                client.getQueryData<Array<PortalSettingsDTO>>([
                    PORTAL_SETTINGS_URL,
                ]) ?? [];
            const newSettings = previousSettings.filter(
                (setting) => setting.id !== id,
            );
            client.setQueryData([PORTAL_SETTINGS_URL], newSettings);

            return { previousSettings, newSettings };
        },
        onError: (_, __, context) => {
            client.setQueryData(
                [PORTAL_SETTINGS_URL],
                context?.previousSettings,
            );
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: [PORTAL_SETTINGS_URL] });
        },
    });

    const setActiveSetting = useMutation({
        mutationFn: portalApi.setActiveSetting,
        onMutate: async ({ id }) => {
            await client.cancelQueries({ queryKey: [PORTAL_SETTINGS_URL] });
            const previousSettings =
                client.getQueryData<Array<PortalSettingsDTO>>([
                    PORTAL_SETTINGS_URL,
                ]) ?? [];
            const newSettings = previousSettings.map((setting) => ({
                ...setting,
                active: setting.id === id,
            }));
            client.setQueryData([PORTAL_SETTINGS_URL], newSettings);

            return { previousSettings, newSettings };
        },
        onError: (_, __, context) => {
            client.setQueryData(
                [PORTAL_SETTINGS_URL],
                context?.previousSettings,
            );
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: [PORTAL_SETTINGS_URL] });
        },
    });

    return { ...query, upsertSetting, deleteSetting, setActiveSetting };
};

export const usePortalSetting = (id?: string) => {
    const portalApi = usePortalApi();
    return useQuery({
        queryKey: [PORTAL_SETTINGS_URL, id],
        queryFn: id ? () => portalApi.fetchSettingById({ id }) : skipToken,
    });
};

export const usePortalFeed = () => {
    const portalApi = usePortalApi();
    return useQuery({
        queryKey: [PORTAL_FEED_URL],
        queryFn: () => portalApi.getPortalFeed(),
    });
};
